import { css } from 'lit';
export const buttonStyles = css `
    :host {
        display: inline-block;
        position: relative;
        width: auto;
        cursor: pointer;
        border-radius: var(--hlx-radius-full);
    }

    .hlx-btn {
        display: inline-flex;
        align-items: stretch;
        justify-content: center;
        width: 100%;
        border-style: solid;
        font-family: var(--hlx-text-font-family-sans-serif);
        font-weight: var(--hlx-text-font-weight-semibold);
        letter-spacing: var(--hlx-text-letter-spacing-loose-1);
        text-decoration: none;
        user-select: none;
        vertical-align: middle;
        padding: 0;
        cursor: inherit;
    }

    .hlx-btn .hlx-btn-icon,
    .hlx-btn.hlx-btn-icon-only .hlx-btn-prefix,
    .hlx-btn.hlx-btn-icon-only .hlx-btn-label,
    .hlx-btn.hlx-btn-icon-only .hlx-btn-suffix {
        display: none;
    }
    .hlx-btn.hlx-btn-icon-only .hlx-btn-icon {
        display: flex;
    }

    .hlx-btn::-moz-focus-inner {
        border: 0;
    }
    .hlx-btn:focus {
        outline: none;
    }
    .hlx-btn:focus-visible {
        outline: 2px solid var(--hlx-color-brand-default);
        outline-offset: 2px;
    }
    .hlx-btn.hlx-btn-inverted:focus-visible {
        outline: 2px solid var(--hlx-color-neutral-0);
        outline-offset: 2px;
    }

    .hlx-btn-prefix,
    .hlx-btn-suffix {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        pointer-events: none;
    }
    .hlx-btn-prefix-slotted {
        padding-right: 8px;
    }
    .hlx-btn-suffix-slotted {
        padding-left: 8px;
    }

    .hlx-btn:not(.hlx-btn-disabled) .hlx-btn-motion-up,
    .hlx-btn:not(.hlx-btn-disabled) .hlx-btn-motion-up-left,
    .hlx-btn:not(.hlx-btn-disabled) .hlx-btn-motion-up-right,
    .hlx-btn:not(.hlx-btn-disabled) .hlx-btn-motion-down,
    .hlx-btn:not(.hlx-btn-disabled) .hlx-btn-motion-down-left,
    .hlx-btn:not(.hlx-btn-disabled) .hlx-btn-motion-down-right,
    .hlx-btn:not(.hlx-btn-disabled) .hlx-btn-motion-left,
    .hlx-btn:not(.hlx-btn-disabled) .hlx-btn-motion-right {
        transition: transform var(--hlx-motion-duration-default) var(--hlx-motion-easing-in-out);
    }
    .hlx-btn:hover:not(.hlx-btn-disabled) .hlx-btn-motion-up {
        transform: translate(0, -2px);
    }
    .hlx-btn:hover:not(.hlx-btn-disabled) .hlx-btn-motion-up-left {
        transform: translate(-2px, -2px);
    }
    .hlx-btn:hover:not(.hlx-btn-disabled) .hlx-btn-motion-up-right {
        transform: translate(2px, -2px);
    }
    .hlx-btn:hover:not(.hlx-btn-disabled) .hlx-btn-motion-down {
        transform: translate(0, 2px);
    }
    .hlx-btn:hover:not(.hlx-btn-disabled) .hlx-btn-motion-down-left {
        transform: translate(-2px, 2px);
    }
    .hlx-btn:hover:not(.hlx-btn-disabled) .hlx-btn-motion-down-right {
        transform: translate(2px, 2px);
    }
    .hlx-btn:hover:not(.hlx-btn-disabled) .hlx-btn-motion-left {
        transform: translate(-2px);
    }
    .hlx-btn:hover:not(.hlx-btn-disabled) .hlx-btn-motion-right {
        transform: translate(2px);
    }

    @media (prefers-reduced-motion) {
        .hlx-btn:hover:not(.hlx-btn-disabled)
            :is(
                .hlx-btn-motion-up,
                .hlx-btn-motion-up-left,
                .hlx-btn-motion-up-right,
                .hlx-btn-motion-down,
                .hlx-btn-motion-down-left,
                .hlx-btn-motion-down-right,
                .hlx-btn-motion-left,
                .hlx-btn-motion-right
            ) {
            transform: none;
        }
    }

    .hlx-btn-label {
        display: inline-flex;
        align-self: center;
        text-align: left;
        line-height: var(--hlx-text-line-height-tight-1);
    }
    .hlx-btn-sm .hlx-btn-label {
        padding: calc(var(--hlx-spacing-sm) - 2px) 0; /* Subtracting the borders */
    }
    .hlx-btn-md .hlx-btn-label {
        padding: calc(var(--hlx-spacing-md) - 2px) 0; /* Subtracting the borders */
    }
    .hlx-btn-sm.hlx-btn-disabled .hlx-btn-label {
        padding: calc(var(--hlx-spacing-sm) - 1px) 0; /* Subtracting the borders */
    }
    .hlx-btn-md.hlx-btn-disabled .hlx-btn-label {
        padding: calc(var(--hlx-spacing-md) - 1px) 0; /* Subtracting the borders */
    }

    .hlx-btn-label-slot {
        /* This is needed to fix the alignment issues when <sup> is used */
        line-height: inherit;
    }

    .hlx-btn-tertiary .hlx-btn-label-slot {
        display: inline;
        position: relative;
    }

    .hlx-btn-tertiary .hlx-btn-label-slot::after {
        content: '';
        position: absolute;
        width: 100%;
        transform: scaleX(0);
        height: 2px;
        bottom: -2px;
        left: 0;
    }
    .hlx-btn-brand.hlx-btn-tertiary .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-brand-default);
    }
    .hlx-btn-dark.hlx-btn-tertiary .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-dark-default);
    }
    .hlx-btn-success.hlx-btn-tertiary .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-success-default);
    }
    .hlx-btn-error.hlx-btn-tertiary .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-error-default);
    }
    .hlx-btn-warning.hlx-btn-tertiary .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-warning-default);
    }
    .hlx-btn-info.hlx-btn-tertiary .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-info-default);
    }
    .hlx-btn-inverted.hlx-btn-tertiary .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-inverted-default);
    }

    .hlx-btn-tertiary:hover:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        transform: scaleX(1);
        transform-origin: bottom left;
        transition: transform var(--hlx-motion-duration-default) var(--hlx-motion-easing-in-out);
    }
    .hlx-btn-tertiary:hover:not(.hlx-btn-disabled):has(.hlx-btn-prefix-slotted):not(
            :has(.hlx-btn-suffix-slotted)
        )
        .hlx-btn-label-slot::after {
        transform-origin: bottom right;
    }
    @media (prefers-reduced-motion) {
        .hlx-btn-tertiary:hover:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
            transform: none;
            transform-origin: unset;
            transition: none;
        }
        .hlx-btn-tertiary:hover:not(.hlx-btn-disabled):has(.hlx-btn-prefix-slotted):not(
                :has(.hlx-btn-suffix-slotted)
            )
            .hlx-btn-label-slot::after {
            transform-origin: unset;
        }
    }
    .hlx-btn-brand.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-brand-hover);
    }
    .hlx-btn-brand.hlx-btn-tertiary:active:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-brand-active);
    }
    .hlx-btn-dark.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-dark-hover);
    }
    .hlx-btn-dark.hlx-btn-tertiary:active:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-dark-active);
    }
    .hlx-btn-success.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-success-hover);
    }
    .hlx-btn-success.hlx-btn-tertiary:active:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-success-active);
    }
    .hlx-btn-error.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-error-hover);
    }
    .hlx-btn-error.hlx-btn-tertiary:active:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-error-active);
    }
    .hlx-btn-warning.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-warning-hover);
    }
    .hlx-btn-warning.hlx-btn-tertiary:active:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-warning-active);
    }
    .hlx-btn-info.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-info-hover);
    }
    .hlx-btn-info.hlx-btn-tertiary:active:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-info-active);
    }
    .hlx-btn-inverted.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-inverted-hover);
    }
    .hlx-btn-inverted.hlx-btn-tertiary:active:not(.hlx-btn-disabled) .hlx-btn-label-slot::after {
        background-color: var(--hlx-button-color-tertiary-inverted-active);
    }

    .hlx-btn-icon {
        display: flex;
        width: 16px;
        justify-content: center;
        align-items: center;
    }

    .hlx-btn-primary {
        border: solid 2px;
    }

    .hlx-btn-secondary {
        background: none;
        border: solid 1.5px;
    }

    .hlx-btn-disabled {
        cursor: not-allowed;
        border-width: 1px;
    }
    .hlx-btn-disabled * {
        pointer-events: none;
    }

    .hlx-btn-brand.hlx-btn-primary {
        background-color: var(--hlx-button-background-color-primary-brand-default);
        border-color: var(--hlx-button-background-color-primary-brand-default);
        color: var(--hlx-button-color-primary-brand-default);
    }
    .hlx-btn-brand.hlx-btn-primary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-brand-hover);
        border-color: var(--hlx-button-background-color-primary-brand-hover);
        color: var(--hlx-button-color-primary-brand-hover);
    }
    .hlx-btn-brand.hlx-btn-primary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-brand-active);
        border-color: var(--hlx-button-background-color-primary-brand-active);
        color: var(--hlx-button-color-primary-brand-active);
    }

    .hlx-btn-brand.hlx-btn-secondary {
        background-color: transparent;
        border-color: var(--hlx-button-border-color-secondary-brand-default);
        color: var(--hlx-button-color-secondary-brand-default);
    }
    .hlx-btn-brand.hlx-btn-secondary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-brand-hover);
        border-color: var(--hlx-button-border-color-secondary-brand-hover);
        color: var(--hlx-button-color-secondary-brand-hover);
    }
    .hlx-btn-brand.hlx-btn-secondary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-brand-active);
        border-color: var(--hlx-button-border-color-secondary-brand-active);
        color: var(--hlx-button-color-secondary-brand-active);
    }

    .hlx-btn-brand.hlx-btn-tertiary {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-brand-default);
    }
    .hlx-btn-brand.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-brand-hover);
    }
    .hlx-btn-brand.hlx-btn-tertiary:active:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-brand-active);
    }

    .hlx-btn-dark.hlx-btn-primary {
        background-color: var(--hlx-button-background-color-primary-dark-default);
        border-color: var(--hlx-button-background-color-primary-dark-default);
        color: var(--hlx-button-color-primary-dark-default);
    }
    .hlx-btn-dark.hlx-btn-primary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-dark-hover);
        border-color: var(--hlx-button-background-color-primary-dark-hover);
        color: var(--hlx-button-color-primary-dark-hover);
    }
    .hlx-btn-dark.hlx-btn-primary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-dark-active);
        border-color: var(--hlx-button-background-color-primary-dark-active);
        color: var(--hlx-button-color-primary-dark-active);
    }

    .hlx-btn-dark.hlx-btn-secondary {
        background-color: transparent;
        border-color: var(--hlx-button-border-color-secondary-dark-default);
        color: var(--hlx-button-color-secondary-dark-default);
    }
    .hlx-btn-dark.hlx-btn-secondary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-dark-hover);
        border-color: var(--hlx-button-border-color-secondary-dark-hover);
        color: var(--hlx-button-color-secondary-dark-hover);
    }
    .hlx-btn-dark.hlx-btn-secondary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-dark-active);
        border-color: var(--hlx-button-border-color-secondary-dark-active);
        color: var(--hlx-button-color-secondary-dark-active);
    }

    .hlx-btn-dark.hlx-btn-tertiary {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-dark-default);
    }
    .hlx-btn-dark.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-dark-hover);
    }
    .hlx-btn-dark.hlx-btn-tertiary:active:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-dark-active);
    }

    .hlx-btn-success.hlx-btn-primary {
        background-color: var(--hlx-button-background-color-primary-success-default);
        border-color: var(--hlx-button-background-color-primary-success-default);
        color: var(--hlx-button-color-primary-success-default);
    }
    .hlx-btn-success.hlx-btn-primary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-success-hover);
        border-color: var(--hlx-button-background-color-primary-success-hover);
        color: var(--hlx-button-color-primary-success-hover);
    }
    .hlx-btn-success.hlx-btn-primary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-success-active);
        border-color: var(--hlx-button-background-color-primary-success-active);
        color: var(--hlx-button-color-primary-success-active);
    }

    .hlx-btn-success.hlx-btn-secondary {
        background-color: transparent;
        border-color: var(--hlx-button-border-color-secondary-success-default);
        color: var(--hlx-button-color-secondary-success-default);
    }
    .hlx-btn-success.hlx-btn-secondary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-success-hover);
        border-color: var(--hlx-button-border-color-secondary-success-hover);
        color: var(--hlx-button-color-secondary-success-hover);
    }
    .hlx-btn-success.hlx-btn-secondary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-success-active);
        border-color: var(--hlx-button-border-color-secondary-success-active);
        color: var(--hlx-button-color-secondary-success-active);
    }

    .hlx-btn-success.hlx-btn-tertiary {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-success-default);
    }
    .hlx-btn-success.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-success-hover);
    }
    .hlx-btn-success.hlx-btn-tertiary:active:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-success-active);
    }

    .hlx-btn-error.hlx-btn-primary {
        background-color: var(--hlx-button-background-color-primary-error-default);
        border-color: var(--hlx-button-background-color-primary-error-default);
        color: var(--hlx-button-color-primary-error-default);
    }
    .hlx-btn-error.hlx-btn-primary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-error-hover);
        border-color: var(--hlx-button-background-color-primary-error-hover);
        color: var(--hlx-button-color-primary-error-hover);
    }
    .hlx-btn-error.hlx-btn-primary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-error-active);
        border-color: var(--hlx-button-background-color-primary-error-active);
        color: var(--hlx-button-color-primary-error-active);
    }

    .hlx-btn-error.hlx-btn-secondary {
        background-color: transparent;
        border-color: var(--hlx-button-border-color-secondary-error-default);
        color: var(--hlx-button-color-secondary-error-default);
    }
    .hlx-btn-error.hlx-btn-secondary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-error-hover);
        border-color: var(--hlx-button-border-color-secondary-error-hover);
        color: var(--hlx-button-color-secondary-error-hover);
    }
    .hlx-btn-error.hlx-btn-secondary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-error-active);
        border-color: var(--hlx-button-border-color-secondary-error-active);
        color: var(--hlx-button-color-secondary-error-active);
    }

    .hlx-btn-error.hlx-btn-tertiary {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-error-default);
    }
    .hlx-btn-error.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-error-hover);
    }
    .hlx-btn-error.hlx-btn-tertiary:active:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-error-active);
    }

    .hlx-btn-warning.hlx-btn-primary {
        background-color: var(--hlx-button-background-color-primary-warning-default);
        border-color: var(--hlx-button-background-color-primary-warning-default);
        color: var(--hlx-button-color-primary-warning-default);
    }
    .hlx-btn-warning.hlx-btn-primary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-warning-hover);
        border-color: var(--hlx-button-background-color-primary-warning-hover);
        color: var(--hlx-button-color-primary-warning-hover);
    }
    .hlx-btn-warning.hlx-btn-primary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-warning-active);
        border-color: var(--hlx-button-background-color-primary-warning-active);
        color: var(--hlx-button-color-primary-warning-active);
    }

    .hlx-btn-warning.hlx-btn-secondary {
        background-color: transparent;
        border-color: var(--hlx-button-border-color-secondary-warning-default);
        color: var(--hlx-button-color-secondary-warning-default);
    }
    .hlx-btn-warning.hlx-btn-secondary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-warning-hover);
        border-color: var(--hlx-button-border-color-secondary-warning-hover);
        color: var(--hlx-button-color-secondary-warning-hover);
    }
    .hlx-btn-warning.hlx-btn-secondary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-warning-active);
        border-color: var(--hlx-button-border-color-secondary-warning-active);
        color: var(--hlx-button-color-secondary-warning-active);
    }

    .hlx-btn-warning.hlx-btn-tertiary {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-warning-default);
    }
    .hlx-btn-warning.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-warning-hover);
    }
    .hlx-btn-warning.hlx-btn-tertiary:active:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-warning-active);
    }

    .hlx-btn-info.hlx-btn-primary {
        background-color: var(--hlx-button-background-color-primary-info-default);
        border-color: var(--hlx-button-background-color-primary-info-default);
        color: var(--hlx-button-color-primary-info-default);
    }
    .hlx-btn-info.hlx-btn-primary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-info-hover);
        border-color: var(--hlx-button-background-color-primary-info-hover);
        color: var(--hlx-button-color-primary-info-hover);
    }
    .hlx-btn-info.hlx-btn-primary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-info-active);
        border-color: var(--hlx-button-background-color-primary-info-active);
        color: var(--hlx-button-color-primary-info-active);
    }

    .hlx-btn-info.hlx-btn-secondary {
        background-color: transparent;
        border-color: var(--hlx-button-border-color-secondary-info-default);
        color: var(--hlx-button-color-secondary-info-default);
    }
    .hlx-btn-info.hlx-btn-secondary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-info-hover);
        border-color: var(--hlx-button-border-color-secondary-info-hover);
        color: var(--hlx-button-color-secondary-info-hover);
    }
    .hlx-btn-info.hlx-btn-secondary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-border-color-secondary-info-active);
        border-color: var(--hlx-button-border-color-secondary-info-active);
        color: var(--hlx-button-color-secondary-info-active);
    }

    .hlx-btn-info.hlx-btn-tertiary {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-info-default);
    }
    .hlx-btn-info.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-info-hover);
    }
    .hlx-btn-info.hlx-btn-tertiary:active:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-info-active);
    }

    .hlx-btn.hlx-btn-primary.hlx-btn-disabled {
        background-color: var(--hlx-button-background-color-primary-disabled);
        border-color: var(--hlx-button-border-color-primary-disabled);
        color: var(--hlx-button-color-primary-disabled);
    }
    .hlx-btn.hlx-btn-secondary.hlx-btn-disabled {
        background-color: var(--hlx-button-background-color-secondary-disabled);
        border-color: var(--hlx-button-border-color-secondary-disabled);
        color: var(--hlx-button-color-secondary-disabled);
    }
    .hlx-btn.hlx-btn-tertiary.hlx-btn-disabled {
        color: var(--hlx-button-color-tertiary-disabled);
    }

    .hlx-btn-inverted.hlx-btn-primary {
        background-color: var(--hlx-button-background-color-primary-inverted-default);
        border-color: var(--hlx-button-border-color-primary-inverted-default);
        color: var(--hlx-button-color-primary-inverted-default);
    }
    .hlx-btn-inverted.hlx-btn-primary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-inverted-hover);
        border-color: var(--hlx-button-border-color-primary-inverted-hover);
        color: var(--hlx-button-color-primary-inverted-hover);
    }
    .hlx-btn-inverted.hlx-btn-primary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-primary-inverted-active);
        border-color: var(--hlx-button-border-color-primary-inverted-active);
        color: var(--hlx-button-color-primary-inverted-active);
    }
    .hlx-btn-inverted.hlx-btn-primary.hlx-btn-disabled {
        background-color: var(--hlx-button-background-color-primary-inverted-disabled);
        border-color: var(--hlx-button-border-color-primary-inverted-disabled);
        color: var(--hlx-button-color-primary-inverted-disabled);
    }

    .hlx-btn-inverted.hlx-btn-secondary {
        background-color: var(--hlx-button-background-color-secondary-inverted-default);
        border-color: var(--hlx-button-border-color-secondary-inverted-default);
        color: var(--hlx-button-color-secondary-inverted-default);
    }
    .hlx-btn-inverted.hlx-btn-secondary:hover:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-secondary-inverted-hover);
        border-color: var(--hlx-button-border-color-secondary-inverted-hover);
        color: var(--hlx-button-color-secondary-inverted-hover);
    }
    .hlx-btn-inverted.hlx-btn-secondary:active:not(.hlx-btn-disabled) {
        background-color: var(--hlx-button-background-color-secondary-inverted-active);
        border-color: var(--hlx-button-border-color-secondary-inverted-active);
        color: var(--hlx-button-color-secondary-inverted-active);
    }
    .hlx-btn-inverted.hlx-btn-secondary.hlx-btn-disabled {
        background-color: var(--hlx-button-background-color-secondary-inverted-disabled);
        border-color: var(--hlx-button-border-color-secondary-inverted-disabled);
        color: var(--hlx-button-color-secondary-inverted-disabled);
    }

    .hlx-btn-inverted.hlx-btn-tertiary {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-inverted-default);
    }
    .hlx-btn-inverted.hlx-btn-tertiary:hover:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-inverted-hover);
    }
    .hlx-btn-inverted.hlx-btn-tertiary:active:not(.hlx-btn-disabled) {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-inverted-active);
    }
    .hlx-btn-inverted.hlx-btn-tertiary.hlx-btn-disabled {
        background-color: transparent;
        border-color: transparent;
        color: var(--hlx-button-color-tertiary-inverted-disabled);
    }

    .hlx-btn-sm {
        height: auto;
        min-height: 48px;
        font-size: var(--hlx-text-font-size-md);
        border-radius: var(--hlx-radius-full);
        line-height: calc(48px - (2px * 2)); /* Subtract the top/bottom border widths */
        padding: 0 calc(var(--hlx-spacing-xl) - 2px); /* Subtract the left/right border widths */
    }
    .hlx-btn-sm.hlx-btn-tertiary {
        font-size: var(--hlx-text-font-size-sm);
        border-radius: var(--hlx-radius-none);
        padding: 0;
    }
    .hlx-btn-sm.hlx-btn-disabled {
        line-height: calc(48px - (1px * 2)); /* Subtract the top/bottom border widths */
        padding: 0 calc(var(--hlx-spacing-lg) - 1px); /* Subtract the left/right border widths */
    }
    .hlx-btn-md {
        height: auto;
        min-height: 56px;
        font-size: var(--hlx-text-font-size-md);
        border-radius: var(--hlx-radius-full);
        line-height: calc(56px - (2px * 2)); /* Subtract the top/bottom border widths */
        padding: 0 calc(var(--hlx-spacing-2xl) - 2px); /* Subtract the left/right border widths */
    }
    .hlx-btn-md.hlx-btn-tertiary {
        border-radius: var(--hlx-radius-none);
        padding: 0;
    }
    .hlx-btn-md.hlx-btn-disabled {
        line-height: calc(56px - (1px * 2)); /* Subtract the top/bottom border widths */
        padding: 0 calc(var(--hlx-spacing-2xl) - 1px); /* Subtract the left/right border widths */
    }
    .hlx-btn-sm.hlx-btn-icon-only:has(.hlx-btn-icon) {
        padding: 0 calc(16px - 2px); /* Subtract the left/right border widths */
    }
    .hlx-btn-sm.hlx-btn-disabled.hlx-btn-icon-only:has(.hlx-btn-icon) {
        padding: 0 calc(16px - 1px); /* Subtract the left/right border widths */
    }
    .hlx-btn-md.hlx-btn-icon-only:has(.hlx-btn-icon) {
        padding: 0 calc(20px - 2px); /* Subtract the left/right border widths */
    }
    .hlx-btn-md.hlx-btn-disabled.hlx-btn-icon-only:has(.hlx-btn-icon) {
        padding: 0 calc(20px - 1px); /* Subtract the left/right border widths */
    }

    .hlx-btn-loading {
        position: relative;
        cursor: wait;
    }

    .hlx-btn-loading .hlx-btn-prefix,
    .hlx-btn-loading .hlx-btn-label,
    .hlx-btn-loading .hlx-btn-suffix,
    .hlx-btn-loading .hlx-btn-icon {
        visibility: hidden;
    }

    .hlx-btn-loading .hlx-btn-loading-wrapper {
        position: absolute;
        width: 100%;
        height: 100%;
        display: inline-flex;
        flex: 1 1 auto;
        justify-content: center;
        align-items: center;
        gap: var(--hlx-spacing-xs);
    }

    .hlx-btn-loading .hlx-btn-loading-spinner {
        font-size: 1em;
        height: 1em;
        width: 1em;
        flex-shrink: 0;
    }

    .hlx-btn-loading .hlx-btn-loading-spinner-margin {
        margin-left: var(--hlx-spacing-md);
    }

    .hlx-btn-loading .hlx-btn-loading-text {
        display: inline-block;
        text-align: left;
        line-height: var(--hlx-text-line-height-tight-1);
        margin-right: var(--hlx-spacing-md);
    }
`;
//# sourceMappingURL=button.styles.js.map